import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PhoneCall, X } from "lucide-react";

export default function UrgenceButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const openPanel = () => setOpen(true);
    window.addEventListener("prosecours:open-urgence", openPanel);
    return () => window.removeEventListener("prosecours:open-urgence", openPanel);
  }, []);

  if (location.pathname.startsWith("/admin")) return null;

  const go = () => {
    sessionStorage.setItem("prosecours_urgence", "Haute");
    window.dispatchEvent(new Event("prosecours:urgence"));
    setOpen(false);
    const scroll = () =>
      document.getElementById("audit")?.scrollIntoView({ behavior: "smooth" });
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 700);
    } else {
      scroll();
    }
  };

  return (
    <div className="relative w-fit" data-testid="urgence-end-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            data-testid="urgence-panel"
            className="absolute bottom-[calc(100%+0.75rem)] right-0 z-30 w-72 bg-[#f7f5f0] border border-[#1a1918]/15 shadow-xl p-5"
          >
            <div className="flex items-center gap-2 overline text-[#ce1e20] font-bold">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ce1e20] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ce1e20]" />
              </span>
              Urgence — rappel sous 2 h
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#1a1918]/80">
              Votre situation ne peut pas attendre ? Laissez vos coordonnées :
              rappel prioritaire sous 2 h ouvrées.
            </p>
            <button
              type="button"
              onClick={go}
              data-testid="urgence-panel-cta"
              className="mt-4 w-full px-4 py-3 bg-[#ce1e20] text-white overline hover:bg-[#a71518] transition-colors"
            >
              Être rappelé en priorité →
            </button>
            <a
              href="tel:+33744883381"
              data-testid="urgence-panel-phone"
              className="mt-3 flex items-center justify-center gap-2 text-sm text-[#0a1e38] hover:text-[#ce1e20] transition-colors"
            >
              <PhoneCall size={14} />
              07 44 88 33 81
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="urgence-button"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        aria-label="Urgence — rappel sous 2 h"
        aria-expanded={open}
        className="relative w-14 h-14 rounded-full bg-[#ce1e20] text-white shadow-lg shadow-[#ce1e20]/30 hover:bg-[#a71518] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center"
      >
        <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ce1e20] opacity-75" />
          <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-white border-2 border-[#ce1e20]" />
        </span>
        {open ? <X size={20} /> : <PhoneCall size={20} strokeWidth={2} />}
      </motion.button>
    </div>
  );
}
